<template>
  <div class="login-box mt-7">
    <div class="login-box-header">
      <h1>{{ $t('signIn.login') }}</h1>
    </div>
    <div class="login-box-body">
      <ValidationObserver ref="signinForm" v-slot="{ invalid }">

        <v-form @submit.prevent>
          <v-card-text class="form">
            <v-container>
              <v-row class="ma-0">
                <transition name="country-code-slide">
                  <v-col cols="3" :md="2" class="pl-0 pt-0 vue-country-code" v-if="showCountryCode">
                    <vue-country-code
                      :disabled-fetching-country="true"
                      :enabled-country-code="true"
                      :preferred-countries="['at', 'de']"
                      default-country="at"
                      @onSelect="onSelectCountryCode"
                    />
                  </v-col>
                </transition>
                <v-col :class="smsEnabled ? 'pr-0 pt-0' : 'pl-0 pr-0 pt-0'">
                  <ValidationProvider ref="identifier" name="identifier" vid="identifier" slim :rules="signinIdentifierRule" v-slot="{ errors, invalid }">
                    <v-text-field
                      name="identifier"
                      v-model="identifier"
                      :error-messages="showIdentifierErrorMessages ? errors[0] : []"
                      type="email"
                      :label="identifierLabel"
                      :disabled="email_identified"
                      :prepend-icon="smsEnabled ? '' : 'person'"
                      @keyup.enter="onEnterAction"
                      @blur="showIdentifierErrorMessages = true">
                      <template v-slot:append-outer>
                        <div class="next-btn-wrapper" v-if="showNextButton">
                          <v-btn type="button" color="primary" :disabled="invalid" @click="enteredEmailOrPhone">{{$t('signIn.next')}}</v-btn>
                        </div>
                      </template>
                    </v-text-field>
                  </ValidationProvider>

                </v-col>
              </v-row>
              <div v-if="showActions" class="actions">
                <div v-if="showActionFields">
                  <ValidationProvider name="password" slim mode="custom" rules="required" v-slot="{ errors }">
                    <v-text-field
                      ref="password"
                      :type="showPassword ? 'text' : 'password'"
                      :label="$t('registerScreen.placeholderPassword')"
                      :error-messages="errors[0]"
                      @click:append="showPassword = !showPassword"
                      :append-icon="showPassword ? icons.mdiEye : icons.mdiEyeOff"
                      prepend-icon="lock"
                      v-model="password"></v-text-field>
                  </ValidationProvider>
                </div>
              </div>
              <!-- security section: enabled when either recaptcha or firebase is enabled -->
              <div v-if="recaptchaEnabled || isFirebaseSmsAuthEnabled">
                <div
                  v-if="showGrecaptcha"
                  ref="login-recaptcha-container"
                  class="g-recaptcha login-recaptcha-container"
                  :data-sitekey="recaptchaSiteKey"
                  data-callback="closeAlert"
                ></div>
                <div v-else-if="showFirebaseRecaptcha" class="login-recaptcha-container" id="recaptcha"></div>

                <v-alert id="recaptcha-alert" class="mt-4 vuetify-alert recaptcha-alert" dense type="warning">
                  {{$t('messages.recaptchaMessage')}}
                </v-alert>
              </div>
              <div v-if="showActions" class="actions">
                <div v-if="showActionFields">
                  <div class="text-center pt-4">
                    <v-btn type="button" @click="login" color="primary" :disabled="invalid" :loading="submitloading">{{ $t('signIn.login') }} </v-btn>
                  </div>
                  <div v-if="(webAppConfig.disableMagicLinkLogin && showOTPLogin) || !webAppConfig.disableMagicLinkLogin" class="text-center marginText">
                    {{ $t('signIn.or') }}
                  </div>
                </div>
                <div v-if="showSigninWithOTPButton" class="text-center">
                  <v-btn type="button" @click="sendOTPToIdentifier" color="simple" :disabled="otpLoading || ($refs.identifier && $refs.identifier.flags.invalid)" :loading="otpLoading">
                    {{ $t('signIn.signin-with-otp') }} </v-btn>
                </div>
                <div v-else-if="!webAppConfig.disableMagicLinkLogin" class="text-center">
                  <v-btn type="button" v-on:click="sendDirectLoginLinkToMail" color="primary" :loading="directLinkLoading">
                    {{ $t('signIn.direct-login-link') }} </v-btn>
                  <div class="text-center marginText dl-desc caption" v-html="$t('signIn.direct-login-link-text-email')" v-if="email_identified"></div>
                  <div class="text-center marginText dl-desc caption" v-html="$t('signIn.direct-login-link-text-sms')" v-else></div>
                </div>
                <div class="text-center pt-4">{{ login_message }}</div>

                <v-card-actions v-if="(!webAppConfig.disableMagicLinkLogin || showOTPLogin)">
                  <a v-on:click.prevent="reset">{{ $t('backButton') }} </a>
                </v-card-actions>
              </div>

            </v-container>
          </v-card-text>
        </v-form>
      </ValidationObserver>

      <p v-if="showOTPLogin && isRegistrationModeSMS" class="col pr-0 pt-0">
        {{ stepOneDescription }}
      </p>
      <span v-if="!isRegistrationModeSMS" class="separator"><i>{{ $t('signIn.or') }}</i></span>
      <div class="login-button-wrapper">
        <div class="login-button-container">
          <facebook-login v-if="showFacebookLogin"/>
          <google-login v-if="showGoogleLogin"/>
          <apple-login v-if="showAppleLogin"/>
          <v-btn v-if="showOTPLogin && isRegistrationModeSMS" :disabled="otpLoading || !identifier || ($refs.identifier && $refs.identifier.flags.invalid)" @click="openTosScreen" color="primary" :loading="otpLoading">
            {{$t('auth.nextButton')}}
          </v-btn>
          <v-btn v-else @click="redirectToRegister" color="primary">
            {{$t('signIn.register')}}
          </v-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script src="https://www.google.com/recaptcha/api.js" async defer></script>
<script>
import {mapGetters, mapState, mapActions} from 'vuex';
import {BASE_API_URL} from '../../store'
import log from 'loglevel'
import _ from 'lodash';
import router from '@/router';
import {mdiPencil, mdiEye, mdiEyeOff} from '@mdi/js';
import {email} from 'vee-validate/dist/rules';
import FacebookLogin from '@/components/auth/FacebookLogin.vue';
import GoogleLogin from '@/components/auth/GoogleLogin.vue';
import {loadRecaptchaScript, showAlert, createCaptchaVerifier, clearCaptchaVerifier, clearGrecaptcha} from '@/firebase'
import {parsePhoneNumberFromString} from 'libphonenumber-js'
import {filterValidChars} from '@/utils'
import AppleLogin from '@/components/auth/AppleLogin.vue'

export default {
  components: {
    FacebookLogin, GoogleLogin, AppleLogin
  },
  data() {
      return {
        directLinkLoading: false,
        email_identified: false,
        icons: {
          mdiPencil, mdiEye, mdiEyeOff
        },
        identifier: '',
        identifierEntered: false,
        login_message: '',
        password: '',
        recaptchaResponse: '',
        showIdentifierErrorMessages: false,
        showPassword: false,
        submitloading: false,
        otpLoading: false,
        countryCode: '',
        firebaseConfirmationResult: null
      };
    },
    computed: {
      ...mapGetters([
        'authSettings',
        'extensionSettings'
      ]),
      ...mapState(['webAppConfig']),
      client_id() {
        return this.$store.state.client_id
      },
      stepOneDescription() {
        return this.smsEnabled ? this.$t('smsSignIn.stepOneDescriptionWithEmail') : this.$t('smsSignIn.stepOneDescription')
      },
      parsedPhoneNumber() {
        if (this.showCountryCode) {
          const parsedNumber = parsePhoneNumberFromString(
            filterValidChars(this.identifier),
            this.countryCode
          )
          if (!parsedNumber || !parsedNumber.isValid()) {
            this.openDialog(
              this.$t('smsSignIn.phoneNumberNotValidTitle'),
              this.$t('smsSignIn.phoneNumberNotValidText')
            )
            return
          }
          return parsedNumber
        }
        return {
          number: this.identifier
        }
      },
      showCountryCode() {
        return this.smsEnabled && this.isNumberInput
      },
      isNumberInput() {
        return /^\d+$/.test(this.identifier.trim())
      },
      isEmailInput() {
        return email.validate(this.identifier)
      },
      showGrecaptcha() {
        return (this.isEmailInput && this.recaptchaEnabled || (this.isNumberInput && this.recaptchaEnabled && !this.isFirebaseSmsAuthEnabled))
      },
      showFirebaseRecaptcha() {
        return this.identifier && this.isFirebaseSmsAuthEnabled
      },
      recaptchaEnabled() {
        return _.get(this.extensionSettings, 'firebase.recaptcha.enabled', false)
      },
      recaptchaSiteKey() {
        return _.get(this.extensionSettings, 'firebase.recaptcha.site_key', '')
      },
      isFirebaseSmsAuthEnabled() {
        return _.get(
          this.authSettings, 'enable_firebase_phone_auth', false
        )
      },
      isOwnSmsAuthEnabled() {
        return _.get(this.authSettings, 'enable_sms_login', true)
      },
      isRegistrationModeSMS() {
        return _.get(this.extensionSettings, 'webview.registration_mode', '') === 'sms'
      },
      smsEnabled() {
        return 'sms' in this.extensionSettings;
      },
      identifierLabel() {
        return this.smsEnabled ? this.$t('signIn.email-or-phone') : this.$t('signIn.email')
      },
      signinIdentifierRule() {
        return this.smsEnabled ? 'required|emailOrPhone' : 'required|email'
      },
      directLinkLoginText() {
        return this.smsEnabled ? this.$t('direct-login-link-text-email-sms') : this.$t('direct-login-link-text-email')
      },
      showFacebookLogin() {
        // when facebook_login is set, but facebook.app_id is missing
        if (_.get(this.authSettings, 'enable_facebook_login', false) && !_.get(this.extensionSettings, 'facebook.app_id')) {
          log.warn('Facebook App ID not set');
        }
        return _.get(this.authSettings, 'enable_facebook_login', false) && _.get(this.extensionSettings, 'facebook.app_id', false);
      },
      showGoogleLogin() {
        const enabledAndroid = _.get(this.authSettings, 'google_login.enabled_android', false);
        const enablediOS = _.get(this.authSettings, 'google_login.enabled_ios', false);
        let web_client_id = _.get(this.authSettings, 'google_login.web_client_id', false);

        // web_client_id is set to "test" on staging
        if (web_client_id === 'test') {
          web_client_id = false
          web_client_id = true
        }

        // when google login is set, but google_login.web_client_id is missing
        if ((enabledAndroid || enablediOS) && !web_client_id) {
          log.warn('Google Web Client ID not set')
        }
        return (enabledAndroid || enablediOS) && web_client_id;
      },
      showAppleLogin() {
        const appleLoginEnabled = _.get(this.authSettings, 'apple_login.enabled', false)
        let webServiceID = _.get(this.authSettings, 'apple_login.web_service_id', false)

        // webServiceID is set to "test" on staging
        if (webServiceID === 'test') {
          webServiceID = true
        }

        // when apple login is set, but apple_login.web_service_id is missing
        if (appleLoginEnabled && !webServiceID) {
          log.warn('Apple Web Client ID not set')
        }

        return appleLoginEnabled && webServiceID
      },
      showOTPLogin() {
        return _.get(this.authSettings, 'otpConfig.enabled', false)
      },
      showNextButton() {
        return !this.identifierEntered && !this.isRegistrationModeSMS && (!this.webAppConfig.disableMagicLinkLogin || this.showOTPLogin)
      },
      showActions() {
        return this.identifierEntered || (this.webAppConfig.disableMagicLinkLogin && !this.showOTPLogin)
      },
      showActionFields() {
        return this.email_identified || (this.webAppConfig.disableMagicLinkLogin && !this.showOTPLogin)
      },
      showSigninWithOTPButton() {
        return this.webAppConfig.disableMagicLinkLogin && this.showOTPLogin
      }
    },
    methods: {
      ...mapActions([
        'requestIdentifierOTP',
        'firebaseRequestOTP',
        'tosScreen'
      ]),
      onEnterAction() {
        if (this.showOTPLogin && this.isRegistrationModeSMS) {
          this.openTosScreen()
        } else {
          this.enteredEmailOrPhone()
        }
      },
      validateRecaptchaResponse() {
        if (this.recaptchaEnabled && window.grecaptcha) {
          this.recaptchaResponse = window.grecaptcha.getResponse()
          if (this.recaptchaResponse.length === 0) {
            showAlert(this.recaptchaEnabled);
            return false;
          }
        }
        return true;
      },
      onSelectCountryCode({iso2}) {
        this.countryCode = iso2
      },
      createGRecaptcha() {
        if (this.recaptchaEnabled && window.grecaptcha === undefined) {
          loadRecaptchaScript()
        }
      },
      async sendDirectLoginLinkToMail() {
        this.directLinkLoading = true;
        try {
          let payload = {};
          if (this.email_identified) {
            payload["email"] = this.identifier;
          } else {
            payload["phone_number"] = this.parsedPhoneNumber.number;
          }

          let response = await this.axios.post(`${BASE_API_URL}/api/v1/clients/${this.client_id}/magic-link`, payload);
          if (response.data.success) {
            this.login_message = this.email_identified ? this.$t('signIn.direct-link-email-send-success') : this.$t('signIn.direct-link-sms-send-success')
          } else if (!response.data.success) {
            this.login_message = this.email_identified ? this.$t('signIn.direct-link-email-send-failed') : this.$t('signIn.direct-link-sms-send-failed')
            if (response.data.code === "USER_DOES_NOT_EXIST") {
              this.login_message = this.$t('signIn.direct-link-user-not-exist')
            }
          }
        } catch (error) {
          log.error(error)
          this.login_message = this.$t('signIn.direct-link-send-unknown-error')
        } finally {
          this.directLinkLoading = false;
        }

      },
      openTosScreen() {
        if(this.isFirebaseSmsAuthEnabled) {
          createCaptchaVerifier()
        }
        this.tosScreen({
          show: true,
          acceptAction: this.handleOTPRequest,
          rejectAction: this.closeTosScreen
        })
      },
      closeTosScreen() {
        this.tosScreen({show: false})
      },
      handleOTPRequest() {
        this.closeTosScreen()
        this.sendOTPToIdentifier()
      },
      setUserIdentifierInStore() {
        const emailIdentified = email.validate(this.identifier)
        if (emailIdentified) {
          this.$store.commit('setUserIdentifierForVerification', this.identifier)
        } else {
          this.$store.commit('setUserIdentifierForVerification', this.parsedPhoneNumber.number || this.identifier)
        }
      },
      async sendOTPToIdentifier() {
        this.otpLoading = true;
        try {
          let payload = {};
          const emailIdentified = email.validate(this.identifier)
          if (emailIdentified && !this.validateRecaptchaResponse()) {
            return;
          }
          if (emailIdentified) {
            payload["email"] = this.identifier
          } else {
            payload["phone_number"] = this.parsedPhoneNumber.number
          }
          if (emailIdentified && this.recaptchaEnabled) {
            payload["recaptchaResponse"] = this.recaptchaResponse
          }

          if (emailIdentified || !this.isFirebaseSmsAuthEnabled) {
            let response = await this.requestIdentifierOTP(payload)
            if (response.data.success) {
              this.setUserIdentifierInStore()
              this.$router.push({name: 'verify-otp-signin', params: {type: 'otp'}})
            } else if (!response.data.success) {
              this.login_message = this.email_identified ? this.$t('verifyOTP.otp-email-send-failed') : this.$t('verifyOTP.otp-sms-send-failed')
            }
          } else if (this.isFirebaseSmsAuthEnabled) {
            this.firebaseConfirmationResult =
              await this.firebaseRequestOTP({
                phoneNumber: this.parsedPhoneNumber.number,
                recaptchaVerifier: window.recaptchaVerifier
              })
              this.setUserIdentifierInStore()
              this.$store.commit('setFirebaseResult', this.firebaseConfirmationResult)
              this.$router.push({name: 'verify-otp-signin', params: {type: 'otp'}})
          }
        } catch (error) {
          this.reset()
          log.error(error)
          this.login_message = this.$t('verifyOTP.otp-send-unknown-error')
        } finally {
          this.otpLoading = false;
        }

      },
      async login() {
        this.submitloading = true;
        try {
          if (!this.validateRecaptchaResponse()) {
            return;
          }
          await this.$store.dispatch('userLogin', {
            email: this.identifier,
            password: this.password,
            recaptchaResponse: this.recaptchaResponse
          });
          if (this.$store.getters.isAuthenticated) {
            if (this.$store.getters.isOpenIdProvider) {
              this.$router.push({name: 'openid'});
            } else {
              this.$router.push({name: 'home'})
            }
          }
        } catch (error) {
          log.error(error)
          this.login_message = this.$t('signIn.login-fail')

        } finally {
          this.submitloading = false;
        }
      },
      async enteredEmailOrPhone() {
        try {
          if (this.identifier) {
            this.login_message = '';
            this.email_identified = email.validate(this.identifier); //;/.+@.+/.test(this.email_or_phone);
            if (this.email_identified) {
              this.createGRecaptcha()
            } else if (this.isFirebaseSmsAuthEnabled) {
              createCaptchaVerifier()
            }
            this.identifierEntered = true;
            this.$nextTick(() => {
              if (this.$refs.password) {
                this.$refs.password.$refs.input.focus();
              }
            })
          }
        } catch (error) {
          log.error(error)
        }
      },
      reset() {
        clearCaptchaVerifier()
        clearGrecaptcha()
        this.identifier = "";
        this.password = "";
        this.identifierEntered = false;
        // this.direct_login_sent_success = null;
        this.login_message = "";
        this.email_identified = false;
        setTimeout(() => {
          this.showIdentifierErrorMessages = false;
        }, 10);
      },
      redirectToRegister() {
        router.push({name: 'register'});
      }

    },
    watch: {
      identifier() {
        // feels like a hack but makes for a better user-experience
        // field listens for user-input but prevents errors until input was valid once
        // "next"-button gets enabled on key-down when valid (would otherwise be on blur)
        this.$nextTick(() => {
          if (this.$refs.identifier.$_veeObserver.fields.identifier.valid && !this.showIdentifierErrorMessages) {
            this.showIdentifierErrorMessages = true;
          }
        });
      }
    }
}
</script>

<style scoped lang="scss">
  .marginText {
    margin-top: 10px;
    margin-bottom: 10px;
  }
  .v-input--checkbox::v-deep p {
    margin-bottom: 0;
  }
  .v-input::v-deep .v-label {
    height: auto;
  }
  .dl-desc {
    max-width: 320px;
    margin-left: auto;
    margin-right: auto;
    text-align: justify
  }
  .v-input--selection-controls {
    margin-bottom: 16px;
  }
  .v-input--selection-controls.v-input::v-deep .v-label {
    animation: none;
  }
  .login-box-body .v-card__text::v-deep .v-input__append-outer {
    margin-top: 0;
    margin-bottom: 0;
  }
  .next-btn-wrapper {
    display: flex;
    justify-content: flex-end;
    .v-btn {
      padding: 0 8px;
      height: 32px;
    }
  }
  .login-button-container {
    display: inline-flex;
    flex-direction: column;
  }
  .login-button-container {
    display: grid;
    gap: 16px;
  }
  .login-button-container::v-deep {
    .v-btn {
      display: block;
      width: 100%;
    }
  }
  .form {
    padding: 0;
    .actions {
      .v-btn {
        width: 100%;
        display: block;
      }
    }
  }
  .login-box {
    .login-recaptcha-container {
      display: flex;
      justify-content: center;
      padding-bottom: 20px;
    }
    .recaptcha-alert {
      display: none;
    }
  }
  .vue-country-code .vue-country-select {
    padding-top: 16px;
    margin-top: 2px;
  }

  .country-code-slide-enter-active,
  .country-code-slide-leave-active {
    transition: all 0.5s ease;
    max-width: 100px;
    overflow: hidden;
  }

  .country-code-slide-enter,
  .country-code-slide-leave-to {
    max-width: 0;
    opacity: 0;
    transform: translateX(-20px);
  }

  .country-code-slide-enter-to,
  .country-code-slide-leave {
    max-width: 100px;
    opacity: 1;
    transform: translateX(0);
  }
</style>
